/*!
 * AudoChat - CRM Customers Management
 * Connected to PostgreSQL Database
 */

import { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import CircularProgress from "@mui/material/CircularProgress";

// Vision UI Dashboard React components
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiButton from "components/VuiButton";
import VuiAvatar from "components/VuiAvatar";

// Vision UI Dashboard React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";
import MiniStatisticsCard from "examples/Cards/StatisticsCards/MiniStatisticsCard";

// Vision UI Dashboard React base styles
import colors from "assets/theme/base/colors";
import linearGradient from "assets/theme/functions/linearGradient";

// React icons
import { BsPeople, BsChatDots, BsSearch, BsEnvelope, BsTelephone } from "react-icons/bs";
import { HiOutlineDotsVertical } from "react-icons/hi";
import { MdAdd } from "react-icons/md";

// API
import api from "utils/api";

const { gradients, info, text, dark, borderCol } = colors;
const { card } = gradients;

// Helper function to format date (Bahrain timezone)
const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", { 
    month: "short", 
    day: "numeric", 
    year: "numeric",
    timeZone: "Asia/Bahrain"
  });
};

// Helper function to format time ago (Bahrain timezone)
const formatTimeAgo = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  const now = new Date();
  
  // Calculate difference considering Bahrain timezone
  const dateBahrain = new Date(date.toLocaleString("en-US", { timeZone: "Asia/Bahrain" }));
  const nowBahrain = new Date(now.toLocaleString("en-US", { timeZone: "Asia/Bahrain" }));
  const diffMs = nowBahrain - dateBahrain;
  
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? "s" : ""} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
  return formatDate(dateString);
};

function Customer({ image, name, email, phone }) {
  return (
    <VuiBox display="flex" alignItems="center" px={1} py={0.5}>
      <VuiBox mr={2}>
        <VuiAvatar src={image} alt={name} size="sm" variant="rounded" />
      </VuiBox>
      <VuiBox display="flex" flexDirection="column">
        <VuiTypography variant="button" color="white" fontWeight="medium">
          {name}
        </VuiTypography>
        {email && (
          <VuiBox display="flex" alignItems="center" gap={0.5} mt={0.25}>
            <BsEnvelope size={12} color="#8492a6" />
            <VuiTypography variant="caption" color="text">
              {email}
            </VuiTypography>
          </VuiBox>
        )}
        {phone && (
          <VuiBox display="flex" alignItems="center" gap={0.5} mt={0.25}>
            <BsTelephone size={12} color="#8492a6" />
            <VuiTypography variant="caption" color="text">
              {phone}
            </VuiTypography>
          </VuiBox>
        )}
      </VuiBox>
    </VuiBox>
  );
}

function Actions({ customerId }) {
  const history = useHistory();
  
  return (
    <VuiBox display="flex" alignItems="center" gap={1}>
      <VuiButton
        variant="text"
        color="info"
        size="small"
        sx={{ minWidth: "auto", p: 0.5 }}
        onClick={() => {
          history.push(`/chat?customer=${customerId}`);
        }}
      >
        <BsChatDots size={16} />
      </VuiButton>
    </VuiBox>
  );
}

function Tables() {
  const [customers, setCustomers] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, newToday: 0 });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [sortBy, setSortBy] = useState("last_activity");
  const [sortOrder, setSortOrder] = useState("DESC");

  const history = useHistory();

  // Load customers from database
  useEffect(() => {
    loadCustomers();
    loadStats();
  }, [sortBy, sortOrder]);

  const loadCustomers = async () => {
    try {
      setLoading(true);
      const customers = await api.getCustomers(searchQuery || undefined, sortBy, sortOrder);
      setCustomers(customers || []);
    } catch (error) {
      console.error("Error loading customers:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const stats = await api.getCustomerStats();
      setStats(stats || { total: 0, active: 0, newToday: 0 });
    } catch (error) {
      console.error("Error loading stats:", error);
    }
  };

  // Reload when search changes (with debounce)
  useEffect(() => {
    const timer = setTimeout(() => {
      loadCustomers();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleMenuOpen = (event) => {
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleSortChange = (sort) => {
    if (sort === "recent") {
      setSortBy("last_activity");
      setSortOrder("DESC");
    } else if (sort === "name") {
      setSortBy("name");
      setSortOrder("ASC");
    } else if (sort === "date") {
      setSortBy("joined_date");
      setSortOrder("DESC");
    }
    handleMenuClose();
  };

  // Prepare table data
  const columns = [
    { name: "customer", align: "left" },
    { name: "joinedDate", align: "center" },
    { name: "lastActivity", align: "center" },
    { name: "actions", align: "center" },
  ];

  const rows = customers.map((customer) => ({
    customer: (
      <Customer
        image={customer.avatar || 'https://uxwing.com/wp-content/themes/uxwing/download/peoples-avatars/no-profile-picture-icon.png'}
        name={customer.name}
        email={customer.email}
        phone={customer.phone}
      />
    ),
    joinedDate: (
      <VuiTypography variant="caption" color="white" fontWeight="medium">
        {formatDate(customer.joined_date)}
      </VuiTypography>
    ),
    lastActivity: (
      <VuiTypography variant="caption" color="text" fontWeight="medium">
        {formatTimeAgo(customer.last_activity)}
      </VuiTypography>
    ),
    actions: <Actions customerId={customer.id} />,
  }));

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <VuiBox py={3}>
        {/* Statistics Cards */}
        <VuiBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6} xl={3}>
              <MiniStatisticsCard
                title={{ text: "Total Customers", fontWeight: "regular" }}
                count={stats.total}
                percentage={{ color: "info", text: "All customers" }}
                icon={{ color: "info", component: <BsPeople size="22px" color="white" /> }}
              />
            </Grid>
            <Grid item xs={12} md={6} xl={3}>
              <MiniStatisticsCard
                title={{ text: "Active Customers" }}
                count={stats.active}
                percentage={{ color: "success", text: "Active status" }}
                icon={{ color: "info", component: <BsChatDots size="22px" color="white" /> }}
              />
            </Grid>
            <Grid item xs={12} md={6} xl={3}>
              <MiniStatisticsCard
                title={{ text: "New Today" }}
                count={stats.newToday}
                percentage={{ color: "info", text: "New signups" }}
                icon={{ color: "info", component: <BsPeople size="22px" color="white" /> }}
              />
            </Grid>
            <Grid item xs={12} md={6} xl={3}>
              <MiniStatisticsCard
                title={{ text: "Total Conversations" }}
                count={customers.length}
                percentage={{ color: "success", text: "All time" }}
                icon={{ color: "info", component: <BsChatDots size="22px" color="white" /> }}
              />
            </Grid>
          </Grid>
        </VuiBox>

        {/* Customers Table */}
        <Card>
          <VuiBox
            p={3}
            sx={{
              background: linearGradient(card.main, card.state, card.deg),
              borderBottom: `1px solid ${borderCol.navbar}`,
            }}
          >
            <VuiBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <VuiTypography variant="lg" color="white" fontWeight="bold">
                Customers & Clients
              </VuiTypography>
              <VuiBox display="flex" alignItems="center" gap={1}>
                <VuiButton
                  variant="gradient"
                  color="info"
                  size="small"
                  startIcon={<MdAdd size={18} />}
                  onClick={() => {
                    // TODO: Add new customer dialog
                    alert("Add new customer functionality - Coming soon");
                  }}
                >
                  Add Customer
                </VuiButton>
                <IconButton
                  onClick={handleMenuOpen}
                  sx={{
                    color: text.main,
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.1)",
                    },
                  }}
                >
                  <HiOutlineDotsVertical />
                </IconButton>
                <Menu
                  anchorEl={menuAnchor}
                  open={Boolean(menuAnchor)}
                  onClose={handleMenuClose}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                >
                  <MenuItem onClick={() => handleSortChange("recent")}>Sort by Recent Activity</MenuItem>
                  <MenuItem onClick={() => handleSortChange("name")}>Sort by Name</MenuItem>
                  <MenuItem onClick={() => handleSortChange("date")}>Sort by Join Date</MenuItem>
                </Menu>
              </VuiBox>
            </VuiBox>

            {/* Search Bar */}
            <VuiBox>
              <TextField
                fullWidth
                placeholder="Search by name, email, or phone..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                size="small"
                InputProps={{
                  startAdornment: (
                    <VuiBox mr={1} display="flex" alignItems="center">
                      <BsSearch size={16} color={text.main} />
                    </VuiBox>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: dark.body,
                    color: "white",
                    borderRadius: "8px",
                    "& fieldset": {
                      borderColor: borderCol.navbar,
                    },
                    "&:hover fieldset": {
                      borderColor: info.main,
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: info.main,
                    },
                  },
                  "& .MuiInputBase-input": {
                    color: "white",
                    fontSize: "14px",
                  },
                  "& .MuiInputBase-input::placeholder": {
                    color: text.main,
                    opacity: 0.7,
                  },
                }}
              />
            </VuiBox>
          </VuiBox>

          <VuiBox
            sx={{
              "& th": {
                borderBottom: ({ borders: { borderWidth }, palette: { grey } }) =>
                  `${borderWidth[1]} solid ${grey[700]}`,
              },
              "& .MuiTableRow-root:not(:last-child)": {
                "& td": {
                  borderBottom: ({ borders: { borderWidth }, palette: { grey } }) =>
                    `${borderWidth[1]} solid ${grey[700]}`,
                },
              },
            }}
          >
            {loading ? (
              <VuiBox display="flex" justifyContent="center" alignItems="center" py={5}>
                <CircularProgress color="info" />
              </VuiBox>
            ) : (
              <Table columns={columns} rows={rows} />
            )}
          </VuiBox>
        </Card>
      </VuiBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Tables;
